/* Account storage is the signed Worker proxy. Browser storage holds recovery intents only. */
(() => {
  const customerId = document.querySelector('meta[name="customer-id"]')?.content || '';
  const nativeFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const ownerKey = 'pc_account_cart_owner', signedOutKey = 'pc_account_cart_signed_out', restoreFailedKey = 'pc_account_cart_restore_failed', unavailableKey = 'pc_account_cart_unavailable';
  // A saved line Shopify cannot restore (sold out, unavailable) fails the whole
  // restore. Remember it per account + saved version so it is not retried (with
  // an error notice) on every page load or edit; any saved change retries once.
  // ponytail: 12h TTL so a restocked line is retried; transient errors are never recorded.
  // Saved lines the Worker reported as unavailable (sold out / short stock / gone) stay
  // saved at full quantity for restock. For 12h the client compares against the
  // saved cart minus them, so they cause no restore loop, notice or divergence.
  // Keyed by the saved digest + sorted variant set; a new digest (edit) re-asks on load.
  const unavailableMarker = () => { try { const m = JSON.parse(storage.getItem(unavailableKey)); return m?.customerId === customerId && Date.now() - m.at < 12 * 36e5 && Array.isArray(m.lines) ? m : null; } catch { return null; } };
  // Saved lines this device kept out after emptying its own cart (the ambiguous case): they stay
  // in the account, are offered quietly ("Add them") and never count as divergence on this device.
  const heldKey = 'pc_account_cart_held';
  const heldKeys = () => { try { const m = JSON.parse(storage.getItem(heldKey)); return m?.customerId === customerId && Date.now() - m.at < 30 * 864e5 && Array.isArray(m.keys) ? m.keys : []; } catch { return []; } };
  const heldCount = () => { const keys = heldKeys(); return (account?.items || []).filter(line => keys.includes(lineKey(line))).reduce((sum, line) => sum + line.quantity, 0); };
  const effective = list => {
    const held = heldKeys();
    if (held.length) list = list.filter(line => !held.includes(lineKey(line)));
    const gaps = unavailableMarker()?.lines || [];
    // Match the exact saved line (variant + properties + plan), never the variant alone.
    return list.map(line => { const gap = gaps.find(g => g.key === lineKey(line)); return gap ? { ...line, quantity: Math.min(line.quantity, gap.available) } : line; }).filter(line => line.quantity > 0);
  };
  const restoreFailed = (digest = account?.digest) => { try { const m = JSON.parse(storage.getItem(restoreFailedKey)); return m?.customerId === customerId && m.digest === digest && Date.now() - m.at < 12 * 36e5; } catch { return false; } };
  const pendingKey = `pc_account_cart_pending_${customerId}`;
  let account, nativeCart, banner, queue = Promise.resolve(), requestQueue = Promise.resolve(), phase = 'loading', restoring = false;
  let cartRenderRevision = 0;
  try { subscribe(PUB_SUB_EVENTS.cartUpdate, () => { cartRenderRevision++; }); } catch {}
  let nativeSettle, savesSent = new Set(), syncJobs = new Map(), accountChain = Promise.resolve(), accountStamp = 0, ownPending = new Set(), retryEdits = new Map(), mergeAdded = [], savedPending = false, guestOwnerAtLoad = null, accountDegraded = false, bootCart, storage, volatilePending, ownerAtBoot, undoNotice, isolatedForAction = false, identityChanged = false, checkoutReturnHold = false;
  const tokenBase = token => String(token || '').split('?')[0];
  const lineKey = line => JSON.stringify([Number(line.id), Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b)), line.selling_plan || null]);
  const items = cart => cart.items.map(line => ({ id: Number(line.variant_id || line.id), quantity: line.quantity,
    properties: line.properties || {}, ...(line.selling_plan_allocation ? { selling_plan: Number(line.selling_plan_allocation.selling_plan.id) } : line.selling_plan ? { selling_plan: Number(line.selling_plan) } : {}) }));
  const same = (a, b) => JSON.stringify(a.map(line => [lineKey(line), line.quantity]).sort()) === JSON.stringify(b.map(line => [lineKey(line), line.quantity]).sort());
  const purchaseAnchor = () => ({ purchaseOperations: [...new Set([...(account?.operations?.filter(op => op.startsWith('purchase:')) || []), ...(account?.purchases?.map(p => p.operation).filter(Boolean) || [])])], purchaseHistoryFrom: account?.purchaseHistoryFrom || new Date(0).toISOString() });
  const purchaseChanged = intent => Array.isArray(intent.purchaseOperations) ?
    account.operations.some(op => op.startsWith('purchase:') && !intent.purchaseOperations.includes(op)) ||
      account.purchases?.some(p => !intent.purchaseOperations.includes(p.operation)) ||
      (account.purchaseHistoryFrom || new Date(0).toISOString()) !== intent.purchaseHistoryFrom
    // No anchor (journaled before the account was ever read): a purchase or cart clear near or
    // after the edit is a change, so the shopper chooses (CART-REVIEW-8: never acknowledge from a
    // watermark). Any clearedThrough at all made every past buyer's Retry ask and refuse the save
    // (F105). startedAt is the DEVICE clock and `at` the server's, so the window is a day wide: a
    // skewed clock still asks; a purchase older than that is a past one. Unreadable date = change.
    : [account.clearedThrough, ...(account.purchases || []).map(p => p.at)].filter(Boolean)
      .some(at => !(Date.parse(at) < (intent.startedAt || 0) - 864e5));
  const lock = fn => navigator.locks.request('pc-account-cart', fn);
  const enqueue = fn => { const task = queue.catch(() => {}).then(() => lock(fn)); queue = task.catch(() => {}); return task; };
  const json = async (url, options) => {
    const result = await nativeFetch(url, { cache: 'no-store', signal: AbortSignal.timeout(5000), ...options });
    const body = await result.json();
    if (!result.ok) throw Object.assign(new Error(body.description || body.error || 'cart_unavailable'), { status: result.status, code: body.error });
    return body;
  };
  // Working reads must not advance the basket still displayed by the theme.
  const readNative = () => json(`${root}cart.js`);
  const readAccount = async () => {
    const result = await json(`${root}apps/pc/cart`);
    if (result.customerId !== customerId || !Array.isArray(result.items)) { identityChanged = true; throw new Error('account_changed'); }
    return result;
  };
  const pending = () => volatilePending || JSON.parse(storage.getItem(pendingKey) || 'null');
  const journal = value => {
    // Storage quota must not become a checkout dependency. Keep this page's
    // intent until the server acknowledges it or the customer chooses a cart.
    volatilePending = value;
    try { storage.setItem(pendingKey, JSON.stringify(value)); volatilePending = undefined; }
    catch { notice(); }
  };
  const forget = id => {
    if (pending()?.operationId !== id) return;
    try { storage.removeItem(pendingKey); volatilePending = undefined; }
    catch { notice(); }
  };

  let noticesTrapped = false;
  function renderNotices() {
    if (window.pcCartNote) {
      window.pcCartNote.reattach();
      // The only ambiguous case: this device emptied its cart and the account still holds lines.
      const offer = heldCount();
      if (offer && document.querySelector('cart-drawer.active')) window.pcCartNote.drawer({ id: 'saved', ms: 0,
        text: `Your saved cart has ${offer} ${offer === 1 ? 'item' : 'items'}`,
        action: ['Add them', () => { try { storage.removeItem(heldKey); } catch {} enqueue(boot).catch(() => notice()); }] });
    }
    const drawer = document.querySelector('cart-drawer.active');
    const container = drawer?.querySelector(drawer.classList.contains('is-empty') ? '.drawer__inner-empty' : '.drawer__inner');
    const host = container || document.getElementById('MainContent') || document.body;
    for (const node of [banner, undoNotice]) if (node && node.parentElement !== host) host.prepend(node);
    // Only touch the drawer focus trap when our actions are in it, or were just
    // removed (stale boundaries); Dawn already traps focus on a plain open.
    const hasNotices = Boolean(container && [banner, undoNotice].some(node => node?.parentElement === container));
    if (container && (hasNotices || noticesTrapped) && typeof trapFocus === 'function') {
      const focus = container.contains(document.activeElement) ? document.activeElement :
        container.querySelector('button:not([disabled]), a[href], input:not([disabled])') || container;
      trapFocus(container, focus);
    }
    noticesTrapped = hasNotices;
  }

  function notice(conflict = false, text) {
    if (conflict !== 'reload') phase = 'degraded';
    if (window.pcCartNote) return managedNotice(conflict, text);
    if (!banner) {
      banner = document.createElement('div');
      banner.className = 'page-width';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = 'padding:16px;margin:16px auto;border:1px solid currentColor';
    }
    banner.replaceChildren();
    let recovery;
    try { recovery = pending(); } catch { recovery = volatilePending; }
    const uncertainPurchase = conflict && recovery?.customerId === customerId && account && purchaseChanged(recovery) && (!Array.isArray(recovery.base) || recovery.uncertain);
    const message = document.createElement('p');
    let actions;
    if (conflict === 'reload') {
      // Customer-facing wording APPROVED by the developer 2026-10-07 ("all defaults ok").
      message.textContent = 'Your cart changed in another tab. Reload the page to continue.';
      actions = [];
    } else if (conflict === 'empty') {
      message.textContent = 'Your current cart is empty. Keep it empty, or restore your saved cart if you have not already purchased those items.';
      actions = [['Keep cart empty', () => resolveConflict(true, true)], ['Restore saved cart', () => resolveConflict(false)]];
    } else if (uncertainPurchase) {
      message.textContent = 'A purchase changed your saved cart. Check that your current cart only contains items you still want to buy, then choose which cart to keep.';
      actions = [['Use current cart', () => resolveConflict(true, true)], ['Use saved cart', () => resolveConflict(false)]];
    } else if (conflict) {
      message.textContent = 'This browser has unsaved cart changes. Choose which cart to keep.';
      actions = [['Keep unsaved cart', () => resolveConflict(true)], ['Use saved cart', () => resolveConflict(false)]];
    } else {
      message.textContent = 'Your cart could not sync with your account. Your current cart is still available.';
      actions = [['Retry', () => enqueue(boot)], ...(customerId && account && ownerAtBoot === customerId ? [['Use current cart', () => resolveConflict(true, true)]] : [])];
    }
    banner.append(message);
    if (text) message.textContent = text;
    for (const [label, action] of actions) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'button button--secondary'; button.textContent = label;
      button.addEventListener('click', () => { button.disabled = true; Promise.resolve(action()).catch(() => notice()).finally(() => { button.disabled = false; }); });
      banner.append(button);
    }
    renderNotices();
  }
  // B1 notice redesign (WIP): in-context notices via assets/pc-cart-notes.js, no page-wide banner.
  function managedNotice(conflict, text) {
    const note = window.pcCartNote;
    if (conflict === 'reload') {
      // Customer-facing wording APPROVED by the developer 2026-10-07 ("all defaults ok").
      note.toast({ text: 'Your cart changed in another tab. Reload the page to continue.', action: ['Reload', () => location.reload()], error: true, persist: true });
      return;
    }
    let recovery;
    try { recovery = pending(); } catch { recovery = volatilePending; }
    const uncertainPurchase = conflict && recovery?.customerId === customerId && account && purchaseChanged(recovery) && (!Array.isArray(recovery.base) || recovery.uncertain);
    const run = fn => () => Promise.resolve(fn()).catch(() => notice());
    // ponytail: the two rare conflict choices keep their existing wording, now as a footnote under the subtotal.
    if (uncertainPurchase) note.footnote({ text: 'A purchase changed your saved cart. Check that your current cart only contains items you still want to buy, then choose which cart to keep.',
      actions: [['Use current cart', run(() => resolveConflict(true, true))], ['Use saved cart', run(() => resolveConflict(false))]] });
    else if (conflict) note.footnote({ text: 'This browser has unsaved cart changes. Choose which cart to keep.',
      actions: [['Keep unsaved cart', run(() => resolveConflict(true))], ['Use saved cart', run(() => resolveConflict(false))]] });
    else if (text) note.toast({ text, error: true });
    else note.footnote({ text: 'Saved to this device only', actions: [['Retry', run(() => enqueue(boot))]] });
  }
  // A stale-tab refusal is already explained by our reload notice: theme add/update
  // handlers see pcHandled and show no generic cart error of their own.
  const staleTab = () => Object.assign(new Error('account_changed'), { pcHandled: true });
  // This device's last synced basket size for this account. An empty native cart is
  // only suspicious (emptied here, e.g. by a checkout) when this device last held items.
  const lastSyncedKey = 'pc_account_cart_last_synced';
  const lastSyncedNonEmpty = () => { try { const m = JSON.parse(storage.getItem(lastSyncedKey)); return m?.customerId === customerId && m.count > 0; } catch { return false; } };
  const healthy = () => {
    phase = 'ready'; banner?.remove(); banner = null; window.pcCartNote?.clearFootnote(); renderNotices();
    if (customerId && nativeCart) try { storage.setItem(lastSyncedKey, JSON.stringify({ customerId, count: nativeCart.item_count })); } catch {}
  };

  // Rebase this user's actual quantity changes, preserving unrelated remote lines.
  function rebase(base, next, remote) {
    const quantities = new Map(base.map(line => [lineKey(line), line.quantity]));
    const changes = new Map(next.map(line => [lineKey(line), { line, delta: line.quantity - (quantities.get(lineKey(line)) || 0) }]));
    for (const line of base) if (!changes.has(lineKey(line))) changes.set(lineKey(line), { line, delta: -line.quantity, remove: true });
    const result = new Map(remote.map(line => [lineKey(line), { ...line }]));
    for (const [key, change] of changes) {
      if (change.remove) { result.delete(key); continue; }
      if (!change.delta) continue;
      const quantity = (result.get(key)?.quantity || 0) + change.delta;
      if (quantity <= 0) result.delete(key);
      else result.set(key, { ...change.line, quantity });
    }
    return [...result.values()];
  }

  async function save(intent, cart, allowRebase = true) {
    account ||= await readAccount();
    savesSent.add(intent.operationId);
    // A purchase is not an acknowledgement of this edit. Without purchased-
    // snapshot provenance, hold older edits for an explicit choice instead of
    // discarding them or automatically putting purchased items back.
    // A saved cart changed elsewhere (pre-save read differs from the marker, or a 409
    // below) re-evaluates unavailable lines: the marker is never carried over it.
    let rebased = Boolean(unavailableMarker() && unavailableMarker().digest !== account.digest);
    for (let attempt = 0; attempt < 3; attempt++) {
      if (account.operations.includes(intent.operationId)) { forget(intent.operationId); return true; }
      if (!intent.registration && purchaseChanged(intent)) { notice(true); return false; }
      if (!allowRebase && !intent.edited && intent.digest !== account.digest) { notice(true); return false; }
      // Remembered quiet-retry edits keep the quantity the shopper chose: rebase them from the saved quantity.
      // A continued intent starts from the last older edit the account already has (F108 priors).
      const acked = (intent.priors || []).filter(prior => account.operations.includes(prior.operationId)).at(-1);
      const start = acked ? acked.items : intent.base;
      const base = intent.edited ? [...start.filter(line => !intent.edited.includes(lineKey(line))),
        ...account.items.filter(line => intent.edited.includes(lineKey(line)))] : start;
      const desired = rebase(base, intent.items, account.items);
      // keepalive: a save started just before a checkout redirect still reaches the Worker.
      const result = await nativeFetch(`${root}apps/pc/cart`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, cache: 'no-store', keepalive: true, signal: AbortSignal.timeout(5000),
        body: JSON.stringify({ customerId, items: desired, digest: account.digest, cartToken: cart.token, operationId: intent.operationId, registration: intent.registration === true }),
      });
      const data = await result.json();
      if (data.customerId && data.customerId !== customerId) throw new Error('account_changed');
      // Another device changed the saved cart: re-evaluate unavailable lines, never carry.
      if (result.status === 409) { account = data; accountStamp++; rebased = true; continue; }
      if (!result.ok || !(data.operations?.includes(intent.operationId) || intent.registration && data.registered === true)) throw new Error('cart_save_failed');
      account = data; accountStamp++; accountDegraded = false; forget(intent.operationId);
      // Our own save keeps known-unavailable saved lines (rebase never sees them): carry the marker.
      const gaps = unavailableMarker();
      if (gaps) try { if (rebased) storage.removeItem(unavailableKey); else storage.setItem(unavailableKey, JSON.stringify({ ...gaps, digest: account.digest })); } catch {}
      return true;
    }
    throw new Error('cart_conflict');
  }

  const pageSections = () => document.querySelector('cart-items')?.getSectionsToRender().filter(s => s.id !== 'cart-live-region-text') || [];
  const sectionNames = () => [...new Set(['cart-icon-bubble', ...(document.querySelector('cart-drawer') ? ['cart-drawer'] : []), ...pageSections().map(s => s.section)])];
  async function refresh(cart) {
    const sections = await json(`${root}cart?sections=${sectionNames().join(',')}`);
    paint(cart, sections);
    nativeCart = cart;
  }
  // Draw already-rendered sections (from /cart?sections= or a mutation's `sections` reply).
  function paint(cart, sections) {
    const revision = ++cartRenderRevision;
    const drawer = document.querySelector('cart-drawer');
    const cartItems = document.querySelector('cart-items');
    const pageSections = cartItems?.getSectionsToRender().filter(s => s.id !== 'cart-live-region-text') || [];
    if (sectionNames().some(name => !sections?.[name])) throw new Error('cart_render_failed');
    if (drawer) { drawer.classList.toggle('is-empty', cart.item_count === 0); drawer.renderContents({ sections }, false); }
    for (const section of pageSections.length ? pageSections : drawer ? [] : [{ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }]) {
      // Empty-page warnings and checkout controls live outside .js-contents.
      const selector = section.id === 'main-cart-items' ? 'cart-items' : section.id === 'main-cart-footer' ? '#main-cart-footer' : section.selector;
      const target = section.id === 'main-cart-items' ? cartItems : document.getElementById(section.id);
      const source = new DOMParser().parseFromString(sections[section.section], 'text/html').querySelector(selector);
      if (target && !source) throw new Error('cart_render_failed');
      if (target) (target.querySelector(selector) || target).innerHTML = source.innerHTML;
    }
    cartItems?.classList.toggle('is-empty', cart.item_count === 0);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
    for (const delay of [0, 200, 500, 900]) setTimeout(() => { try { window.baCurr?.rePeat?.(); } catch {} }, delay);
    // Optional PDP refreshes must never hold or downgrade the accepted cart operation.
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // Undo's Add reply has sections but no full basket for cartUpdate consumers.
        const basket = Array.isArray(cart.items) ? Promise.resolve(cart) : readNative();
        basket.then(cartData => {
          if (revision === cartRenderRevision) return publish(PUB_SUB_EVENTS.cartUpdate, { source: 'pc-account-cart', cartData: cartData });
        }).catch(() => {});
      }
    } catch {}
  }

  async function restore(desired, current) {
    if (same(effective(desired), items(current))) return current;
    restoring = true;
    try {
      // Browser cart mutations emit native Shopify shopping events even with
      // our restore header. Restore this same native cart through the Worker.
      for (let attempt = 0; attempt < 3; attempt++) {
        const result = await nativeFetch(`${root}apps/pc/cart`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(5000),
          body: JSON.stringify({ action: 'restore', customerId, digest: account.digest, items: items(current), cartToken: current.token, operationId: crypto.randomUUID() }),
        });
        const data = await result.json();
        if (data.customerId && data.customerId !== customerId) throw new Error('account_changed');
        if (result.status === 409 && data.error === 'cart_changed') {
          account = data; desired = account.items; current = await readNative(); continue;
        }
        if (!result.ok || data.customerId !== customerId || data.restored !== true) throw new Error('cart_restore_incomplete');
        // Worker pruned saved lines Shopify cannot sell now (sold out / unavailable) and
        // returned the pruned saved cart: adopt it silently (no existing notice fits).
        if (data.reason === 'lines_unavailable' && Array.isArray(data.unavailable)) {
          const lines = data.unavailable.map(u => ({ variantId: String(u.variantId), available: Math.max(0, Number(u.available) || 0),
            key: lineKey({ id: u.variantId, properties: u.properties || {}, ...(u.sellingPlan ? { selling_plan: Number(u.sellingPlan) } : {}) }) }))
            .sort((a, b) => a.key.localeCompare(b.key));
          try { storage.setItem(unavailableKey, JSON.stringify({ customerId, digest: data.digest ?? account.digest, ids: lines.map(l => l.variantId).join(','), lines, at: Date.now() })); } catch {}
          desired = effective(desired); // adopt silently (no existing notice fits); the saved cart is unchanged
        } else {
          // A clean restore means every saved line is back (e.g. a restock): drop the marker.
          try { storage.removeItem(unavailableKey); } catch {}
        }
        const restored = await readNative();
        await refresh(restored);
        if (!same(desired, items(restored))) throw Object.assign(new Error('cart_restore_incomplete'), { unrestorable: true });
        return restored;
      }
      throw new Error('cart_conflict');
    } catch (error) {
      if (error.unrestorable) try { storage.setItem(restoreFailedKey, JSON.stringify({ customerId, digest: account?.digest ?? null, at: Date.now() })); } catch {}
      // Redraw from a fresh read; if that read fails the caller keeps its own post-mutation cart.
      try { await refresh(await readNative()); error.redrawn = true; } catch {}
      notice(); throw error;
    }
    finally { restoring = false; }
  }

  async function resolveConflict(keepPending, useCurrent = false) {
    return enqueue(async () => {
      const intent = pending(); account = await readAccount(); accountDegraded = false; let cart = await readNative();
      if (useCurrent || keepPending && intent?.customerId === customerId) {
        const purchased = intent && purchaseChanged(intent);
        // Preserve the edit delta rather than relabeling a bought snapshot as
        // a new whole-cart intent. Unknown bases cannot establish that delta.
        if (!useCurrent && purchased && (!Array.isArray(intent.base) || intent.uncertain)) { notice(true); return; }
        let chosen = useCurrent || intent.registration || account.operations.includes(intent.operationId) ? items(cart) : intent.items || items(cart);
        if (!useCurrent && purchased) {
          if (!Array.isArray(account.purchases) || !Array.isArray(intent.purchaseOperations) ||
              (account.purchaseHistoryFrom || new Date(0).toISOString()) !== intent.purchaseHistoryFrom ||
              account.purchases.some(p => typeof p.operation !== 'string')) { notice(true); return; }
          const bought = new Map();
          const tokenDigest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(tokenBase(intent.cartToken)));
          const tokenHash = [...new Uint8Array(tokenDigest)].map(b => b.toString(16).padStart(2, '0')).join('');
          for (const purchase of account.purchases.filter(p => p.token === tokenHash && !intent.purchaseOperations.includes(p.operation))) for (const [hash, quantity] of purchase.lines) bought.set(hash, (bought.get(hash) || 0) + quantity);
          const unbought = [];
          for (const line of intent.items) {
            const base = intent.base.find(l => lineKey(l) === lineKey(line))?.quantity || 0;
            const properties = Object.fromEntries(Object.keys(line.properties || {}).sort().map(key => [key, line.properties[key]]));
            const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([Number(line.id), properties, line.selling_plan || null])));
            const hash = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
            const quantity = line.quantity - Math.min(Math.max(0, line.quantity - base), Math.max(0, (bought.get(hash) || 0) - base));
            if (quantity) unbought.push({ ...line, quantity });
          }
          chosen = rebase(intent.base, unbought, account.items);
        }
        const fresh = { ...intent, ...purchaseAnchor(), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(), digest: account.digest, base: account.items, items: chosen, cartToken: tokenBase(cart.token), registration: false, uncertain: false };
        journal(fresh); if (await save(fresh, cart, false) === false) return;
      }
      cart = await restore(account.items, cart);
      if (intent) forget(intent.operationId);
      await register(cart); storage.setItem(ownerKey, customerId); storage.removeItem(signedOutKey); ownerAtBoot = customerId; checkoutReturnHold = false; healthy();
    });
  }
  // The saved cart's digest (Shopify's compareDigest of the whole stored value, snapshots and
  // tokens included) after this token's last confirmed registration. An unchanged digest for the
  // same token means the Worker would answer "already registered": skip that page-load round trip.
  const registeredKey = 'pc_account_cart_registered';
  const alreadyRegistered = cart => {
    try {
      const last = JSON.parse(storage.getItem(registeredKey));
      return last?.customerId === customerId && last.digest === account.digest && last.token === tokenBase(cart.token) && same(effective(account.items), items(cart));
    } catch { return false; }
  };
  async function register(cart) {
    // A token may only be bound to the exact basket it represents. A CAS race
    // requires restoring the new saved version before trying registration again.
    for (let attempt = 0; attempt < 3; attempt++) {
      cart = await restore(account.items, cart);
      if (alreadyRegistered(cart)) return cart;
      const intent = { ...purchaseAnchor(), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(), digest: account.digest, base: account.items, items: account.items, cartToken: tokenBase(cart.token), registration: true };
      journal(intent);
      if (await save(intent, cart, false)) {
        try { storage.setItem(registeredKey, JSON.stringify({ customerId, digest: account.digest, token: tokenBase(cart.token) })); } catch {}
        return cart;
      }
    }
    throw new Error('cart_conflict');
  }
  async function boot() {
    // Signed-out pages never sync (E10): init released the owner with no Worker read.
    if (!customerId) { healthy(); return; }
    // A device signed out of a DIFFERENT account is treated as that owner
    // (privacy first: clear, never adopt). Same account: adopt or prompt.
    let marker = null;
    try { marker = JSON.parse(storage.getItem(signedOutKey)); } catch {}
    const markerOwner = typeof marker?.owner === 'string' ? marker.owner : '';
    // The 30-day expiry applies only to the same-account empty-cart prompt; a
    // different-account marker always clears (no cross-account adoption).
    const signedOut = markerOwner === customerId && Date.now() - marker.at < 30 * 864e5 ? markerOwner : null;
    const owner = storage.getItem(ownerKey) || (markerOwner && markerOwner !== customerId ? markerOwner : '');
    ownerAtBoot = owner || '';
    phase = 'loading';
    // Verify cookie identity before any destructive native mutation. A stale
    // page's customer meta and local owner do not establish server identity.
    account = await readAccount(); accountDegraded = false;
    let cart = await readNative();
    nativeCart = bootCart = cart;
    // Operator E10 (2026-10-06): logout keeps the device cart, as Shopify's own
    // cart cookie does. Only a direct switch to ANOTHER signed-in account clears it.
    if (owner && customerId && owner !== customerId) {
      if (cart.item_count) {
        const cleared = await json(`${root}apps/pc/cart`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'clear', customerId, digest: account?.digest ?? null, items: items(cart), cartToken: cart.token, operationId: crypto.randomUUID() }),
        });
        if (cleared.customerId !== customerId || cleared.cleared !== true) throw new Error('account_changed');
        cart = await readNative();
        if (cart.item_count) throw new Error('cart_restore_incomplete');
      }
      await refresh(cart);
    }
    let recovery = pending();
    // F108: an edit handed to a checkout before its account reply arrived (Buy Now; the save is sent
    // with keepalive). When the Worker acknowledged it and that basket is gone (an empty cart under a
    // new token: bought), the account already holds the edit: done, not a conflict to ask about.
    // An unacknowledged intent keeps the purchase-aware recovery below.
    // An unacknowledged ADD (Buy Now redirects as soon as Shopify accepts it; its save may never
    // have left the page) with its basket gone the same way: silent only with proof. A recorded
    // purchase since the intent's anchor means the checkout bought it (the purchase history clears
    // bought lines). Without proof (an expired or cleared cart cookie) a lone add, whose earlier
    // edits the account already has, is saved silently so nothing is lost. Anything else, such as
    // a continued intent carrying an unacknowledged Remove, keeps the existing question below.
    const basketGone = recovery?.customerId === customerId && !recovery.registration && recovery.cartToken &&
      !cart.item_count && tokenBase(cart.token) !== recovery.cartToken;
    // A recorded purchase silences only an add whose earlier edits the account already has: an
    // unacknowledged earlier Remove keeps the question, so a removed item cannot come back.
    const acknowledged = basketGone && account.operations.includes(recovery.operationId);
    const priorsAcked = basketGone && (recovery.priors || []).every(prior => account.operations.includes(prior.operationId));
    const loneAdd = basketGone && recovery.add && priorsAcked && !recovery.uncertain && Array.isArray(recovery.items);
    if (acknowledged || (basketGone && recovery.add && priorsAcked && purchaseChanged(recovery))) {
      forget(recovery.operationId); recovery = null;
    } else if (loneAdd) {
      // Inside the usual 120 s rebase window only: an older add whose saved cart changed since (a
      // Remove on another device) is asked about rather than re-adding the item.
      if (await save(recovery, cart, Date.now() - recovery.startedAt < 120000) === false) return;
      recovery = null;
    }
    if (recovery?.customerId === customerId) {
      if (account.operations.includes(recovery.operationId) || recovery.registration) {
        if (tokenBase(cart.token) !== recovery.cartToken || !recovery.items || !same(recovery.items, items(cart))) { notice(true); return; }
        forget(recovery.operationId);
      }
      else {
        if (recovery.uncertain) { notice(true); return; }
        if (tokenBase(cart.token) !== recovery.cartToken || (recovery.items && !same(recovery.items, items(cart)))) { notice(true); return; }
        recovery.items ||= items(cart);
        if (await save(recovery, cart, Date.now() - recovery.startedAt < 120000) === false) return;
      }
    }
    // A first sign-in still adopts this device's cart after its remembered edits are saved.
    if (!owner && cart.item_count && (recovery?.customerId !== customerId || recovery.edited)) {
      // One-time adoption: keep the greater quantity rather than double an
      // existing account line seen in an older, unmarked browser cart.
      const merged = new Map(account.items.map(line => [lineKey(line), line]));
      for (const line of items(cart)) if ((merged.get(lineKey(line))?.quantity || 0) < line.quantity) merged.set(lineKey(line), line);
      // Lines the shopper changed during a quiet-retry window keep the chosen quantity.
      for (const [key, line] of retryEdits) if (line.quantity) merged.set(key, line); else merged.delete(key);
      // What the merge adds on top of this device's cart (for the merge note's Undo).
      const device = new Map(items(cart).map(line => [lineKey(line), line.quantity]));
      mergeAdded = [...merged.values()].filter(line => line.quantity > (device.get(lineKey(line)) || 0))
        .map(line => ({ key: lineKey(line), quantity: device.get(lineKey(line)) || 0, added: line.quantity - (device.get(lineKey(line)) || 0) }));
      const intent = { ...purchaseAnchor(), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(), digest: account.digest, base: account.items, items: [...merged.values()], cartToken: tokenBase(cart.token) };
      journal(intent); if (await save(intent, cart) === false) return;
    }
    // An empty cart after a signed-out visit may mean a guest checkout bought
    // the saved lines: ask (Keep empty / Restore) instead of restoring them.
    // A saved marker from another version (edit elsewhere) is re-asked once on this load.
    if (unavailableMarker() && unavailableMarker().digest !== account.digest) try { storage.removeItem(unavailableKey); } catch {}
    // Prompt only when this device emptied its own synced basket, or after a signed-out
    // visit (a guest checkout may have bought the saved lines). A device that was already
    // empty (another device added) adopts the account cart silently.
    if (((owner === customerId && lastSyncedNonEmpty()) || signedOut === customerId) && !cart.item_count && effective(account.items).length && !recovery) {
      // Keep this device empty without asking.
      if (signedOut === customerId) {
        // After a signed-out visit (bought as guest): stay silent; the first edit replaces those lines.
        savedPending = true; phase = 'ready'; renderNotices(); return;
      }
      // Ambiguous (this device emptied its own cart): hold the saved lines in the account, offer
      // them quietly in the drawer, and merge later edits into the saved cart without restoring them.
      try { storage.setItem(heldKey, JSON.stringify({ customerId, keys: effective(account.items).map(lineKey), at: Date.now() })); } catch {}
    }
    // Returning device, or a first sign-in whose window edits did not reach the adoption (e.g. the
    // shopper emptied the cart): the remembered edits are applied before any restore.
    if (retryEdits.size && (owner === customerId || !owner) && !recovery) {
      const desired = new Map(account.items.map(line => [lineKey(line), line]));
      for (const [key, line] of retryEdits) if (line.quantity) desired.set(key, line); else desired.delete(key);
      if (!same([...desired.values()], account.items)) {
        const intent = { ...purchaseAnchor(), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(), digest: account.digest, base: account.items, items: [...desired.values()], cartToken: tokenBase(cart.token) };
        journal(intent); if (await save(intent, cart) === false) return;
      }
    }
    if (!restoreFailed() || same(account.items, items(cart))) {
      cart = await restore(account.items, cart);
      if (account.items.length || account.digest !== null) cart = await register(cart);
    }
    storage.setItem(ownerKey, customerId); storage.removeItem(signedOutKey);
    ownerAtBoot = customerId;
    nativeCart = cart;
    retryEdits = new Map();
    healthy();
    // Capture this merge's lines: mergeAdded is reset right after, before any Undo click.
    const addedLines = mergeAdded; mergeAdded = [];
    const added = addedLines.reduce((sum, line) => sum + line.added, 0);
    // The lines stay available until the Undo succeeds, so a failed Undo can be tried again.
    if (added && window.pcCartNote) window.pcCartNote.merge({ count: added, onUndo: () => undoMerge(addedLines).then(() => { addedLines.length = 0; },
      error => { window.pcCartNote.toast({ text: 'This item could not be restored. Please try again.', error: true }); throw error; }) });
  }

  const renderedRow = body => [...document.querySelectorAll('.cart-item')].find(node => body?.id ? node.dataset.cartLineKey === String(body.id) :
    node.id === `CartItem-${body?.line}` || node.id === `CartDrawer-Item-${body?.line}`);
  // Title, thumbnail and list position of the line being removed (read before Shopify re-renders).
  function renderedMeta(body) {
    // ponytail: best effort; any missing piece just falls back to a generic row.
    try {
      const row = renderedRow(body);
      if (!row) return {};
      return { title: row.querySelector('.cart-item__name')?.textContent?.trim(),
        index: [...(row.parentElement?.children || [])].filter(node => node.classList?.contains('cart-item')).indexOf(row) };
    } catch { return {}; }
  }
  function renderedLine(body) {
    const row = renderedRow(body);
    // ponytail: nested bundle lines get no Undo (re-adding cannot restore the
    // parent relationship); a parent's Undo re-adds only the parent line.
    if (!row?.dataset.cartLineVariant || row.classList?.contains('cart-item__nested-line')) return;
    try {
      const plan = row.dataset.cartLinePlan;
      // Liquid's `item.properties | json` renders an empty set as [], which /cart/add.js rejects (400).
      const properties = JSON.parse(row.dataset.cartLineProperties || '{}');
      return { id: Number(row.dataset.cartLineVariant), quantity: Number(row.querySelector('[name="updates[]"]')?.getAttribute('value')) || 1,
        properties: properties && !Array.isArray(properties) ? properties : {}, ...(plan ? { selling_plan: Number(plan) } : {}) };
    } catch { return; }
  }

  // Dawn publishes cartUpdate after it re-renders the lines; fall back to a short wait.
  const afterRender = () => new Promise(resolve => {
    let off; const done = () => { off?.(); clearTimeout(t); resolve(); };
    const t = setTimeout(done, 1200);
    try { off = typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? subscribe(PUB_SUB_EVENTS.cartUpdate, () => requestAnimationFrame(done)) : null; } catch {}
  });
  // Undo a sign-in merge: set exactly the lines the merge added back to this device's quantities,
  // in one request keyed by Shopify line key, drawn from its own sections reply.
  async function undoMerge(lines) {
    document.querySelector('cart-drawer')?.invalidateRender?.();
    const native = await readNative(), updates = {};
    for (const line of lines) {
      const row = native.items.find(current => lineKey(items({ items: [current] })[0]) === line.key);
      if (row) updates[row.key] = line.quantity;
    }
    if (!Object.keys(updates).length) return;
    const result = await window.fetch(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates, sections: sectionNames(), sections_url: location.pathname }) });
    if (!result.ok) throw new Error('undo_failed');
    const reply = await result.json();
    try { paint(reply, reply.sections); } catch { await refresh(await readNative()); }
  }
  async function undoRemoval(item) {
    document.querySelector('cart-drawer')?.invalidateRender?.();
    // Re-add only what is missing. Signed in, the client tops up from its own pre-edit read
    // (X-PC-Undo-Target); a guest edit has no such read, so read here.
    let quantity = item.quantity;
    if (!customerId) {
      const current = items(await readNative()).find(line => lineKey(line) === lineKey(item));
      quantity = Math.max(0, item.quantity - (current?.quantity || 0));
      if (!quantity) return refresh(await readNative());
    }
    // X-PC-Undo: funnel-tracking skips its AddToCart for a restore (F23).
    // F108: the add brings back the rendered sections, so the line is drawn without two more reads.
    const result = await window.fetch(`${root}cart/add.js`, { method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-PC-Undo': '1', ...(customerId ? { 'X-PC-Undo-Target': String(item.quantity) } : {}) },
      body: JSON.stringify({ items: [{ ...item, quantity }], sections: sectionNames(), sections_url: location.pathname }) });
    if (!result.ok) throw new Error('undo_failed');
    // A restore cannot empty the cart; any drawing problem falls back to a full refresh.
    try { paint({ item_count: 1 }, (await result.json()).sections); }
    catch { await refresh(await readNative()); }
  }
  function offerUndo(item, meta = {}) {
    if (window.pcCartNote) {
      // Shown only after Shopify confirmed the removal and the lines re-rendered.
      // The restored line is found by identity (variant + properties + plan), not position.
      const isLine = row => {
        const props = JSON.parse(row.dataset.cartLineProperties || '{}');
        return lineKey({ id: Number(row.dataset.cartLineVariant), properties: props && !Array.isArray(props) ? props : {},
          ...(row.dataset.cartLinePlan ? { selling_plan: Number(row.dataset.cartLinePlan) } : {}) }) === lineKey(item);
      };
      afterRender().then(() => window.pcCartNote.removedRow({ index: meta.index ?? 0, title: meta.title || 'Item', isLine,
        onUndo: () => undoRemoval(item).catch(error => { window.pcCartNote.toast({ text: 'This item could not be restored. Please try again.', error: true }); throw error; }) }));
      return;
    }
    // ponytail: one undo on this page, replaced by the next removal.
    undoNotice?.remove();
    const node = document.createElement('div'), message = document.createElement('p'), button = document.createElement('button');
    node.className = 'page-width'; node.setAttribute('role', 'status');
    message.textContent = 'Item removed from your cart.';
    button.type = 'button'; button.className = 'button button--secondary'; button.textContent = 'Undo';
    button.addEventListener('click', async () => {
      document.querySelector('cart-drawer')?.invalidateRender?.();
      button.disabled = true;
      try {
        const current = items(await readNative()).find(line => lineKey(line) === lineKey(item));
        const quantity = Math.max(0, item.quantity - (current?.quantity || 0));
        if (quantity) {
          const result = await window.fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-PC-Undo': '1' }, body: JSON.stringify({ items: [{ ...item, quantity }] }) });
          if (!result.ok) throw new Error('undo_failed');
        }
        await refresh(await readNative()); node.remove();
        if (undoNotice === node) undoNotice = undefined;
        renderNotices();
      } catch { message.textContent = 'This item could not be restored. Please try again.'; }
      finally { button.disabled = false; }
    });
    node.append(message); node.append(button);
    undoNotice = node; renderNotices();
  }

  async function verifyShoppingIdentity() {
    if (identityChanged) throw new Error('account_changed');
    // B6: a few-byte static section, not a full page render (~771 KB homepage).
    const result = await nativeFetch(`${root}?section_id=pc-cart-identity&pc_cart_identity=${crypto.randomUUID()}`, { cache: 'no-store', signal: AbortSignal.timeout(5000) });
    if (!result.ok || (result.url && new URL(result.url).origin !== location.origin)) throw new Error('account_changed');
    const marker = new DOMParser().parseFromString(await result.text(), 'text/html').querySelector('meta[name="pc-cart-customer"]');
    // A 200 can still carry a failed/null render: that is unavailable, not a different customer.
    if (!marker) throw new Error('identity_unavailable');
    if (marker.content !== customerId) { identityChanged = true; throw new Error('account_changed'); }
  }

  async function isolateForShopping() {
    // Automatic cleanup remains server-side. If it failed, a new shopping
    // action may start an isolated native cart after a fresh Shopify identity
    // check, without consulting or mutating any previous account storage.
    await verifyShoppingIdentity();
    const cart = await json(`${root}cart/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
    if (!Array.isArray(cart.items) || cart.item_count || (await readNative()).item_count) throw new Error('cart_restore_incomplete');
    await refresh(cart);
    try { storage.removeItem(ownerKey); if (customerId) { storage.setItem(ownerKey, customerId); storage.removeItem(signedOutKey); } }
    catch { isolatedForAction = true; notice(); }
    ownerAtBoot = customerId;
    if (customerId) notice(); else healthy();
  }

  // A failed quiet retry turns the window edits into a journal entry (as degraded edits are), so
  // the next sync or Retry applies the chosen quantities over the saved cart. Cleared only after.
  async function rememberEdits() {
    if (!retryEdits.size || (ownerAtBoot && ownerAtBoot !== customerId)) return;
    let cart = nativeCart;
    try { cart = await readNative(); } catch {}
    if (!cart) return;
    let old = null;
    try { old = pending(); } catch {}
    if (old?.customerId !== customerId) old = null;
    journal({ ...(old || { ...(account ? purchaseAnchor() : {}), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(),
      digest: account?.digest ?? null, base: items(cart) }), items: items(cart), cartToken: tokenBase(cart.token),
      edited: [...new Set([...(old?.edited || []), ...retryEdits.keys()])] });
    retryEdits = new Map();
  }

  // 3: nothing escapes the chain (no unhandled rejection; idle() always resolves).
  function queueSync(job) {
    ownPending.add(job.intent.operationId); syncJobs.set(job.intent.operationId, job);
    accountChain = accountChain.catch(() => {}).then(() => syncAccount(job)).catch(() => {});
  }
  // Buy Now leaves for checkout as soon as its add is in: if this page's journaled edit has not
  // been sent yet (its account read still pending), send it once with keepalive. CAS-safe: only
  // with a known digest; a stale digest is refused by the Worker and the next load resolves it.
  // Flushed on pagehide and on visibilitychange to hidden: pagehide alone misses a mobile app switch
  // or kill. One small body per edit (keepalive bodies share a 64 KiB in-flight budget); savesSent
  // prevents a second send, and the Worker is idempotent by operationId.
  const flushUnsent = () => {
    try {
      const intent = pending();
      if (!intent || intent.customerId !== customerId || !ownPending.has(intent.operationId) || savesSent.has(intent.operationId)) return;
      if (!intent.items || intent.uncertain || !account?.digest || account.operations.includes(intent.operationId) || purchaseChanged(intent)) return;
      const acked = (intent.priors || []).filter(prior => account.operations.includes(prior.operationId)).at(-1);
      const desired = rebase(acked ? acked.items : intent.base, intent.items, account.items);
      const payload = JSON.stringify({ customerId, items: desired, digest: account.digest, cartToken: intent.cartToken, operationId: intent.operationId, registration: false });
      if (payload.length > 16384) return; // ponytail: a cart this large saves on the next load instead
      savesSent.add(intent.operationId);
      nativeFetch(`${root}apps/pc/cart`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, cache: 'no-store', keepalive: true, body: payload }).catch(() => {});
    } catch {}
  };
  window.addEventListener?.('pagehide', flushUnsent);
  document.addEventListener?.('visibilitychange', () => { if (document.visibilityState === 'hidden') flushUnsent(); });

  // F108: the account half of a signed-in edit, on its own chain (never the native lock). Mirrors
  // what the mutation path did inline after the native edit: uncertain and unavailable notices,
  // save, reconcile with the saved cart. Off the shopper's path, it waits for the account read
  // (5 s budget) instead of the old 1 s grace: a slow read is not an outage.
  async function syncAccount({ intent, after, accountRead, accountResult, accountStampAtRead, uncertain, redrawFailed, clear }) {
    const own = () => { try { return pending(); } catch { return volatilePending; } };
    try {
      // A later edit on this page continued this intent (it is now one of its priors): that save carries both.
      if (own()?.operationId !== intent.operationId) return;
      let timer;
      const fresh = accountResult || await Promise.race([accountRead, new Promise(resolve => { timer = setTimeout(resolve, 5000); })]);
      clearTimeout(timer);
      const accountUnavailable = !fresh?.value;
      // Keep the last known account (its purchase anchor stays valid); a slow or failed read only
      // marks later edits uncertain until a fresh read or Retry. Never replace an account a save
      // returned after this read started.
      if (accountUnavailable) accountDegraded = true;
      else { if (!accountResult && accountStamp === accountStampAtRead) account = fresh.value; accountDegraded = false; }
      if (accountUnavailable) { notice(); return; }
      if (uncertain) { notice(true); return; }
      // The known-failed check uses the account version BEFORE this edit's save.
      const knownFailed = restoreFailed(account?.digest);
      try {
        if (clear) intent.base = account?.items || intent.base;
        if (await save(intent, after) === false) return;
        // Carry a known failure forward to the version this edit saved (same TTL).
        if (knownFailed) try { const m = JSON.parse(storage.getItem(restoreFailedKey)); storage.setItem(restoreFailedKey, JSON.stringify({ ...m, digest: account.digest })); } catch {}
        if (!same(effective(account.items), items(after)) && !knownFailed) {
          // Reconcile under the native lock, against the basket as it is now, and only when no
          // later edit of this page is still unsaved (that edit reconciles after its own save).
          await enqueue(async () => {
            if (own()) return;
            const restored = await register(await restore(account.items, await readNative()));
            try { await refresh(restored); } catch { redrawFailed = true; }
            nativeCart = restored;
          });
        }
        if (redrawFailed) notice(); else healthy();
      // Shopify's result was already returned unchanged; a failed save or restore only degrades.
      } catch { accountDegraded = true; notice(); }
    } finally {
      ownPending.delete(intent.operationId);
      // Kept until the account has it: a skipped (superseded) job may be queued again.
      if (account?.operations?.includes(intent.operationId)) syncJobs.delete(intent.operationId);
    }
  }

  // idle(): resolves once queued native edits and their account saves (F108) are done.
  const api = window.pcAccountCart = { getStatus: () => ({ phase, customerId }), retry: () => enqueue(boot), renderNotices, idle: async () => { let tail; do { tail = accountChain; await Promise.all([queue, tail]); } while (tail !== accountChain); } };

  // B6: checkout never waits on a fetch, lock or account sync, and never fails
  // closed. Positional updates[] are disabled during the submit event, so the
  // HTML entry list (built after this event, skipping disabled controls) omits
  // them and a stale tab cannot write its quantities into the session's cart.
  // Shopify then checks out the cookie session's real cart and customer.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.defaultPrevented || form.tagName !== 'FORM' || form.method.toUpperCase() !== 'POST' || event.submitter?.name !== 'checkout') return;
    const action = new URL(form.action, location.href);
    if (action.origin !== location.origin || action.pathname.replace(/\/$/, '') !== `${root}cart`.replace(/\/$/, '')) return;
    // An edit still inside Dawn's 300 ms change debounce (value != rendered
    // value attribute) is sent by native line key, never by position.
    const changed = [];
    const quantities = [...form.elements].filter(input => input.name === 'updates[]' && !input.disabled);
    for (const input of quantities) {
      const key = input.closest?.('.cart-item')?.dataset.cartLineKey;
      if (key && input.value !== input.getAttribute('value')) { input.name = `updates[${key}]`; changed.push(input); }
      else input.disabled = true;
    }
    // Next task, not a microtask: a user-initiated submit drains microtasks before the entry list is built.
    setTimeout(() => {
      quantities.forEach(input => { input.disabled = false; });
      changed.forEach(input => { input.name = 'updates[]'; });
    });
  }, true);

  try { storage = window.localStorage; storage.setItem('pc_cart_storage_test', '1'); storage.removeItem('pc_cart_storage_test'); if (!navigator.locks?.request) throw new Error('locks_unavailable'); }
  catch { if (customerId) notice(); api.ready = Promise.resolve(); return; }
  // Guests with no previous account on this device need nothing: no reads, no lock.
  if (!customerId) {
    // E10: logout keeps the device cart. Release ownership locally (no Worker
    // read, no wait) and remember who signed out for the next login.
    let owner = null;
    try { owner = storage.getItem(ownerKey); if (owner) storage.removeItem(ownerKey); } catch {}
    // The owner goes first: a failed marker write must never leave it set (that blocks signed-out Add).
    if (owner) try { storage.setItem(signedOutKey, JSON.stringify({ owner, at: Date.now() })); } catch {}
    // Whatever is still stored now (a failed removeItem) never counts as a new sign-in.
    try { guestOwnerAtLoad = storage.getItem(ownerKey); } catch {}
    ownerAtBoot = ''; phase = 'ready'; api.ready = Promise.resolve();
  }
  else api.ready = enqueue(boot).catch(() => {
    if (!customerId) return;
    // No notice yet (unless an inner step already showed one, e.g. a failed restore):
    // edits go through natively and are journaled until the quiet retry.
    if (phase !== 'degraded') phase = 'retrying';
    // A failed first sync is often transient: a background tab (throttled timers and fetches
    // around the 5 s budgets) or a brief network/Worker hiccup. Retry once quietly before any
    // footnote: when a hidden tab becomes visible, else after a short pause.
    // The retry also redraws, so a first failure in rendering is not hidden by a clean second sync.
    // A failed retry journals the window edits (rememberEdits) for the next sync or Retry (F106).
    const retryOnce = () => enqueue(async () => { await boot(); if (nativeCart) await refresh(nativeCart); }).catch(() => enqueue(rememberEdits).catch(() => {}).then(() => notice()));
    if (document.visibilityState === 'hidden') {
      const onVisible = () => { if (document.visibilityState === 'hidden') return; document.removeEventListener?.('visibilitychange', onVisible); retryOnce(); };
      document.addEventListener('visibilitychange', onVisible);
    } else setTimeout(retryOnce, 1500);
  });

  const accountFetch = async function(input, options) {
    let displayedCart = nativeCart; const displayedPhase = phase, displayedRestore = restoring;
    let url, kind, body, targetedKey, encoded;
    const rewriteBody = value => {
      const headers = new Headers(options?.headers || input.headers);
      if (encoded) {
        const form = encoded instanceof FormData ? new FormData() : new URLSearchParams();
        for (const [name, entry] of encoded) if (name !== 'line' && name !== 'id' && !/^updates(?:\[.*\])?$/.test(name)) form.append(name, entry);
        if (value.line) form.set('line', value.line);
        else if (value.id) form.set('id', value.id);
        if (value.updates) for (const [key, quantity] of Object.entries(value.updates)) form.set(`updates[${key}]`, quantity);
        if (form instanceof FormData) headers.delete('Content-Type');
        return { ...options, headers, body: form };
      }
      headers.set('Content-Type', 'application/json');
      return { ...options, headers, body: JSON.stringify(value) };
    };
    // F109: Shopify answers 400 "no valid id or line parameter" when an edit targets a line that is
    // already gone (a repeat Remove, a stale drawer, a quantity change on a line removed elsewhere).
    // Answer with the current cart (and the sections the caller asked for) instead of a raw error:
    // a removal already holds, and a quantity change cannot apply to a line that is no longer there.
    // Any other result, including every other rejection, is Shopify's original.
    const settle = async (result, cart) => {
      try {
        if (kind !== 'change' || result?.status !== 400 || body?.id == null) return result;
        cart ||= await readNative();
        if (cart.items.some(line => line.key === String(body.id) || String(line.variant_id) === String(body.id))) return result;
        // Line keys also change when discount applications change (our second-bottle discount), not
        // only on removal. Find the line by its full identity (variant + properties + plan, from the
        // rendered row): exactly one match is sent once more with its new key.
        if (!new Headers(options?.headers || input.headers).has('X-PC-Rekeyed')) {
          let identity;
          try {
            const row = renderedRow(body);
            if (row?.dataset.cartLineVariant) {
              const properties = JSON.parse(row.dataset.cartLineProperties || '{}');
              identity = lineKey({ id: Number(row.dataset.cartLineVariant), properties: properties && !Array.isArray(properties) ? properties : {},
                ...(row.dataset.cartLinePlan ? { selling_plan: Number(row.dataset.cartLinePlan) } : {}) });
            }
          } catch {}
          // Without the full identity (no rendered row) never guess by variant: quiet redraw.
          const matches = identity ? cart.items.filter(line => lineKey(items({ items: [line] })[0]) === identity) : [];
          if (matches.length === 1) return Object.assign(result, { pcRekey: matches[0].key });
        }
        // Zero matches: already gone (a Remove is done). Several: ambiguous, never guess.
        const names = Array.isArray(body.sections) ? body.sections : typeof body.sections === 'string' && body.sections ? body.sections.split(',') : [];
        const sections = names.length ? await json(`${root}cart?sections=${names.join(',')}`) : undefined;
        return new Response(JSON.stringify({ ...cart, ...(sections ? { sections } : {}) }), { status: 200, headers: { 'Content-Type': 'application/json' } });
      } catch { return result; }
    };
    try {
      url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, location.href);
      kind = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?$/)?.[1];
      if (url.origin !== location.origin || !kind || (options?.method || input.method || 'GET').toUpperCase() !== 'POST') return nativeFetch(input, options);
      if (new Headers(options?.headers || input.headers).get('X-PC-Cart-Restore') === '1') return nativeFetch(input, options);
    } catch { return nativeFetch(input, options); }
    // Attribute-only cart writes (funnel-tracking _visitor_id/__fbp/click ids) never change lines:
    // send them at once, as without this client, so a Buy Now redirect cannot outrun them.
    if (kind === 'update' && typeof options?.body === 'string') {
      try { const parsed = JSON.parse(options.body); if (parsed && typeof parsed === 'object' && !('updates' in parsed)) return nativeFetch(input, options); } catch {}
    }
    // Reserve invocation order before asynchronous Request body decoding.
    // Each edit still targets the basket captured above, not a queued result.
    const previousRequest = requestQueue;
    let finishRequest;
    requestQueue = new Promise(resolve => { finishRequest = resolve; });
    try {
      await previousRequest;
      let payload = options?.body;
      const contentType = new Headers(options?.headers || input.headers).get('Content-Type') || '';
      if (payload == null && typeof input.clone === 'function') {
        payload = /multipart\/form-data/i.test(contentType) ? await input.clone().formData() : await input.clone().text();
      }
      if (payload instanceof FormData || payload instanceof URLSearchParams) encoded = payload;
      else if (typeof payload === 'string' && /application\/x-www-form-urlencoded/i.test(contentType)) encoded = new URLSearchParams(payload);
      if (encoded) {
        body = Object.fromEntries(encoded);
        if (encoded.has('updates[]')) body.updates = encoded.getAll('updates[]');
        else {
          const updates = [...encoded].filter(([name]) => /^updates\[.+\]$/.test(name));
          if (updates.length) body.updates = Object.fromEntries(updates.map(([name, quantity]) => [name.slice(8, -1), quantity]));
        }
      } else { try { body = JSON.parse(payload); } catch {} }
      if (kind === 'update' && body && !('updates' in body)) { finishRequest(); return nativeFetch(input, options); }
    } catch (error) { finishRequest(); return nativeFetch(input, options); }
    try {
    if (!customerId) {
      // A sign-in in another tab makes this signed-out page stale: never edit
      // the account's session cart from it (no network cost).
      let owner = null;
      try { owner = storage.getItem(ownerKey); } catch {}
      if (owner && owner !== guestOwnerAtLoad) { notice('reload'); return Promise.reject(staleTab()); }
    }
    if (!customerId && ownerAtBoot === '' && phase === 'ready') {
      // Plain guest: native request, no queue/lock; Undo reads the rendered row.
      const item = kind === 'change' && Number(body?.quantity) === 0 ? renderedLine(body) : undefined;
      const meta = item ? renderedMeta(body) : undefined;
      const result = await nativeFetch(input, options);
      if (result.ok && item) offerUndo(item, meta);
      return settle(result);
    }
    if (customerId && (displayedPhase === 'loading' || displayedRestore) && (body?.line || Array.isArray(body?.updates))) {
      // Wait for the first sync instead of rejecting; only a basket the sync
      // actually changed makes the displayed line numbers unsafe.
      await api.ready;
      // First sync failed and a quiet retry is pending: edit the native cart now (journaled);
      // the retry reconciles it with the account.
      if (phase === 'retrying') displayedCart = nativeCart || await readNative().catch(() => undefined);
      else if (restoring || !nativeCart || !bootCart || !same(items(bootCart), items(nativeCart))) {
        // Only claim an account update when the sync actually changed the basket.
        if (bootCart && nativeCart && !restoring) notice(false, 'Your cart was updated from your account. Review it and try again.');
        else notice();
        return Promise.reject(new Error('cart_sync_pending'));
      }
      displayedCart = nativeCart;
    }
    // Line numbers refer to the basket displayed when the request was made,
    // not a basket changed by an earlier queued edit or remote restoration.
    if (customerId && displayedPhase === 'retrying' && !displayedCart && (body?.line || Array.isArray(body?.updates))) displayedCart = await readNative().catch(() => undefined);
    // An Undo add was handed over before its basket read finished (F108): number lines from that read.
    if (customerId && nativeSettle && (body?.line || Array.isArray(body?.updates))) { await nativeSettle.catch(() => {}); displayedCart = nativeCart; }
    if (customerId && (body?.line || Array.isArray(body?.updates))) {
      if (!displayedCart || (body.line && !displayedCart.items[body.line - 1]) ||
          (Array.isArray(body.updates) && body.updates.length !== displayedCart.items.length)) {
        notice(); return Promise.reject(new Error('cart_line_missing'));
      }
      body = { ...body };
      if (body.line) {
        const key = displayedCart.items[body.line - 1].key;
        // Shopify requires a positional line for selling-plan changes. Keep
        // its key so the position can be resolved against the queued cart.
        if ('selling_plan' in body) targetedKey = key;
        else { body.id = key; delete body.line; }
      }
      if (Array.isArray(body.updates)) body.updates = Object.fromEntries(displayedCart.items.map((line, i) => [line.key, body.updates[i]]));
      options = rewriteBody(body);
    }
    // F108: the caller gets Shopify's result as soon as the native edit is done: a change/update/clear
    // at once (its reply is the cart; nativeCart is set to it before the caller draws it, so later
    // line numbers map to the lines on screen), an Undo or section-rendering add at once with its
    // basket read following (line-numbered edits wait for that read), Buy Now and other section-less
    // adds right after their basket read and journal write. The account read, save and any
    // reconcile run afterwards on their own chain.
    let deliver, handedOver = false;
    const delivered = new Promise(resolve => { deliver = resolve; });
    const undoAdd = kind === 'add' && new Headers(options?.headers || input.headers).get('X-PC-Undo') === '1';
    // An add that asks for rendered sections (the product form, drawer upsell) stays on this page
    // and draws from its own reply: hand it over before the basket read, like Undo. Buy Now and
    // other section-less adds keep waiting for the read and journal write before they redirect.
    const earlyAdd = undoAdd || (kind === 'add' && body?.sections != null);
    const task = api.ready.then(() => enqueue(async () => {
      if (identityChanged) { notice(); throw new Error('account_changed'); }
      // The account read runs beside the native mutation; a slow or down Worker
      // never delays Add/qty/Remove (checked after the mutation, 1 s grace).
      const accountStampAtRead = accountStamp;
      const accountRead = customerId ? readAccount().then(value => ({ value }), error => ({ error })) : null;
      let owner, oldPending, storageUncertain = false;
      try { owner = storage.getItem(ownerKey) || ''; oldPending = pending(); }
      catch { owner = ownerAtBoot; oldPending = volatilePending; storageUncertain = true; notice(); }
      if (isolatedForAction) owner = ownerAtBoot;
      const removed = cart => {
        if (kind !== 'change' || Number(body?.quantity) !== 0 || !cart) return;
        const row = body.id ? cart.items.find(line => line.key === String(body.id)) : cart.items[body.line - 1];
        return row && !row.parent_relationship && items({ items: [row] })[0];
      };
      // Signed out (E10): shop on the kept device cart; never clear or isolate it.
      if (!customerId) {
        let item;
        try { if (kind === 'change' && Number(body?.quantity) === 0) item = removed(await readNative()); } catch {}
        const meta = item ? renderedMeta(body) : undefined;
        const result = await nativeFetch(input, options);
        if (result.ok && item) offerUndo(item, meta);
        return settle(result);
      }
      if (owner !== customerId && !(owner === '' && ownerAtBoot === '')) {
        if (!ownerAtBoot || ownerAtBoot === customerId) { notice('reload'); throw staleTab(); }
        try { await isolateForShopping(); oldPending = undefined; }
        catch (error) { notice(); throw error; }
      }
      let before;
      try { before = await readNative(); } catch { /* journal an uncertain intent before forwarding */ }
      // F108: a signed-in Undo sends the line's target quantity; top it up from this read
      // instead of a second basket read in undoRemoval. Never guess without a read.
      const undoTarget = kind === 'add' && Number(new Headers(options?.headers || input.headers).get('X-PC-Undo-Target'));
      if (undoTarget) {
        const line = body?.items?.length === 1 && body.items[0];
        if (!line || !before) throw new Error('undo_failed');
        const quantity = undoTarget - (items(before).find(current => lineKey(current) === lineKey(line))?.quantity || 0);
        if (quantity <= 0) return Response.json({ items: [] }); // already back (another tab): nothing to add
        options = rewriteBody({ ...body, items: [{ ...line, quantity }] });
      }
      const undoItem = removed(before), undoMeta = undoItem ? renderedMeta(body) : undefined;
      // Kept-empty device (post-purchase or ambiguous): the first edit adopts this device's cart.
      const adoptDevice = savedPending && account;
      if (adoptDevice) savedPending = false;
      // Quiet-retry window (first sync failed): the edit goes only to Shopify and the changed lines
      // are remembered with the quantity the shopper chose; the retry applies them over the saved
      // cart (they win over a merge or restore). If the retry fails they become a journal entry
      // (rememberEdits) that the next sync or Retry applies the same way.
      if (phase === 'retrying') {
        const result = await nativeFetch(input, options);
        // Only with a known pre-edit basket: an unknown one cannot say which lines the shopper changed.
        if (before) try {
          const after = await readNative(); nativeCart = after;
          const was = new Map(items(before).map(line => [lineKey(line), line]));
          const now = new Map(items(after).map(line => [lineKey(line), line]));
          for (const [key, line] of now) if ((was.get(key)?.quantity || 0) !== line.quantity) retryEdits.set(key, line);
          for (const [key, line] of was) if (!now.has(key)) retryEdits.set(key, { ...line, quantity: 0 });
        } catch { /* the retry restores from the account if this read failed */ }
        if (result.ok && undoItem) offerUndo(undoItem, undoMeta);
        return settle(result);
      }
      let uncertain = storageUncertain || !account || !before || checkoutReturnHold;
      let accountResult;
      // F108: this page's own edit whose account save is still queued is continued below (its
      // journal entry becomes a prior of this one), never recovered here: a recovery save would put
      // a Worker call in front of this native edit.
      const ownOld = oldPending && ownPending.has(oldPending.operationId);
      if (oldPending?.customerId === customerId && !ownOld) {
        // Recovery needs the account before forwarding: reuse the read already in flight.
        accountResult = await accountRead;
        try {
          if (accountResult.error) throw accountResult.error;
          account = accountResult.value; accountDegraded = false;
          if (!before || oldPending.uncertain || tokenBase(before.token) !== oldPending.cartToken ||
              (oldPending.items && !same(oldPending.items, items(before)))) uncertain = true;
          else {
            oldPending.items ||= items(before);
            if (await save(oldPending, before, Date.now() - oldPending.startedAt < 120000) === false) uncertain = true;
          }
        } catch { uncertain = true; }
      }
      if (identityChanged) { notice(); throw new Error('account_changed'); }
      // A degraded account counts once the recovery above had its chance: a transient slow read
      // that the recovery just repaired is not a reason to ask the shopper which cart to keep.
      uncertain ||= accountDegraded;
      const continuing = oldPending?.customerId === customerId && !account?.operations.includes(oldPending.operationId);
      const provenContinuation = continuing && !oldPending.uncertain && before && Array.isArray(oldPending.base) &&
        oldPending.items && same(oldPending.items, items(before)) && tokenBase(before.token) === oldPending.cartToken;
      if (targetedKey) {
        if (!before) { notice(); throw new Error('cart_sync_pending'); }
        const index = before.items.findIndex(line => line.key === targetedKey);
        if (index < 0) return Response.json({ errors: 'This cart item changed. Refresh your cart and try again.', description: 'This cart item changed. Refresh your cart and try again.' }, { status: 422 });
        options = rewriteBody({ ...body, line: index + 1 });
      }
      // A continued intent saves from the older base; `priors` lets save() start from the last of
      // those older edits the account already acknowledged (a save still in flight), never twice.
      const intent = { ...purchaseAnchor(), customerId, operationId: crypto.randomUUID(), startedAt: Date.now(), digest: account?.digest ?? null,
        base: before ? items(before) : [], items: null, cartToken: before ? tokenBase(before.token) : null, uncertain, add: kind === 'add',
        ...(continuing ? { base: oldPending.base, purchaseOperations: oldPending.purchaseOperations, purchaseHistoryFrom: oldPending.purchaseHistoryFrom,
          startedAt: oldPending.startedAt, edited: oldPending.edited, uncertain: provenContinuation ? storageUncertain || !account || accountDegraded || checkoutReturnHold : true,
          priors: [...(oldPending.priors || []), ...(oldPending.items ? [{ operationId: oldPending.operationId, items: oldPending.items }] : [])] } : {}) };
      // Saving against the full saved cart as base makes the account equal this device's cart.
      if (adoptDevice) intent.base = account.items;
      journal(intent);
      let result, transportError;
      try { result = await nativeFetch(input, options); }
      catch (error) { transportError = error; }
      const handOver = cart => { handedOver = true; nativeCart = cart; deliver(result); if (undoItem) offerUndo(undoItem, undoMeta); };
      if (kind !== 'add' && result?.ok && !transportError) {
        try {
          const cart = await result.clone().json();
          if (Array.isArray(cart?.items) && cart.token) handOver(cart);
        } catch { /* not a cart reply: hand over after the read below */ }
      }
      // An add reply is not a cart: an Undo or section-rendering add is handed over now and the basket
      // read at once; line-numbered edits wait for that read. Buy Now (no sections) is handed over
      // after the read and the journal write below, so its checkout redirect leaves a complete intent.
      let earlyAfter;
      if (earlyAdd && result?.ok && !transportError) {
        // Until the read lands, the journal holds the pre-add basket plus the reply's lines (Shopify
        // answers an add with each added line at its final quantity; a single form add answers with
        // the line itself), so a checkout during the read can still flush a complete save (pagehide)
        // and a return finds a lone add with items. The fresh basket below replaces it.
        if (before) try {
          const reply = await result.clone().json();
          const lines = Array.isArray(reply?.items) ? reply.items : reply?.variant_id || reply?.id ? [reply] : [];
          if (lines.length && lines.every(line => Number.isFinite(Number(line.quantity)))) {
            const merged = new Map(items(before).map(line => [lineKey(line), line]));
            for (const line of items({ items: lines })) merged.set(lineKey(line), line);
            // This page's own edit from now on: a pagehide during the read may flush it.
            intent.items = [...merged.values()]; journal(intent); ownPending.add(intent.operationId);
          }
        } catch { /* no usable reply: the read below fills the journal */ }
        const read = nativeSettle = readNative();
        handedOver = true; deliver(result);
        try { earlyAfter = await read; nativeCart = earlyAfter; } catch {}
        finally { if (nativeSettle === read) nativeSettle = undefined; }
      }
      try {
        // Shopify can mutate the cart before returning 422/400, or before the
        // response connection fails. Only a fresh basket can prove no change.
        let after;
        try { after = earlyAfter || await readNative(); intent.items = items(after); journal(intent); }
        catch { ownPending.delete(intent.operationId); notice(); return result; }
        if (!handedOver && result?.ok && !transportError) handOver(after);
        if (!result?.ok && before && tokenBase(before.token) === tokenBase(after.token) && same(items(before), items(after))) {
          if (oldPending) {
            journal(oldPending);
            // A's own save may have been skipped while this intent continued it: queue it again
            // (a second run finds A saved and does nothing).
            if (ownOld && syncJobs.has(oldPending.operationId)) queueSync(syncJobs.get(oldPending.operationId));
          } else forget(intent.operationId);
          nativeCart = after;
          if (transportError) notice();
          return settle(result, after);
        }
        let redrawFailed = false;
        if (!result?.ok) {
          // Error consumers do not render their response. Show what actually
          // landed without converting a rejected/partial add into success.
          try { await refresh(after); } catch { redrawFailed = true; notice(); }
        }
        if (!handedOver && undoItem && !items(after).some(line => lineKey(line) === lineKey(undoItem))) offerUndo(undoItem, undoMeta);
        nativeCart = after;
        // F108: the account side (read grace, save, reconcile) runs on its own chain, so the next
        // native edit never waits on the Worker. Saves stay in order; a superseded one is skipped
        // because the later intent carries it (last state wins, CAS digest chain kept).
        queueSync({ intent, after, accountRead, accountResult, accountStampAtRead, uncertain, redrawFailed, clear: kind === 'clear' });
        return result;
      } finally { if (transportError) throw transportError; }
    }));
    return await Promise.race([task, delivered]);
    } finally { finishRequest(); }
  };

  // F109: an identical line change already in flight (a repeat click on Remove, or Enter on a
  // focused control while Dawn shows its spinner) shares the first request's result instead of
  // sending a second one, which Shopify would reject for a line that is already gone.
  const inflight = new Map();
  const changeKey = (input, options) => {
    try {
      const url = new URL(typeof input === 'string' || input instanceof URL ? String(input) : input.url, location.href);
      if (url.origin !== location.origin || !/\/cart\/change(?:\.js)?$/.test(url.pathname) || (options?.method || '').toUpperCase() !== 'POST') return;
      const raw = options?.body;
      const body = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return Object.fromEntries(new URLSearchParams(raw)); } })()
        : raw instanceof FormData || raw instanceof URLSearchParams ? Object.fromEntries(raw) : null;
      if (!body || (body.id == null && body.line == null) || body.quantity == null) return;
      return JSON.stringify([String(body.id ?? ''), String(body.line ?? ''), Number(body.quantity), body.selling_plan ?? null]);
    } catch {}
  };
  // The same change, sent once more with the line's new key (marked so it is never re-keyed again).
  const rekeyed = (options, key) => {
    const headers = new Headers(options?.headers); headers.set('X-PC-Rekeyed', '1');
    const raw = options?.body;
    if (raw instanceof FormData) { const form = new FormData(); for (const [name, value] of raw) form.append(name, value); form.set('id', key); return { ...options, headers, body: form }; }
    if (raw instanceof URLSearchParams) { const form = new URLSearchParams(raw); form.set('id', key); return { ...options, headers, body: form }; }
    try { return { ...options, headers, body: JSON.stringify({ ...JSON.parse(raw), id: key }) }; }
    catch { const form = new URLSearchParams(raw); form.set('id', key); return { ...options, headers, body: form.toString() }; }
  };
  window.fetch = function(input, options) {
    const key = changeKey(input, options);
    if (!key) return accountFetch.call(this, input, options);
    if (inflight.has(key)) return inflight.get(key).then(response => response.clone());
    const own = accountFetch.call(this, input, options).then(response => response?.pcRekey ? accountFetch.call(this, input, rekeyed(options, response.pcRekey)) : response);
    // Cloned before the first caller reads the body (then callbacks run in registration order).
    const shared = own.then(response => response.clone());
    shared.catch(() => {});
    inflight.set(key, shared);
    own.finally(() => { if (inflight.get(key) === shared) inflight.delete(key); }).catch(() => {});
    return own;
  };
})();
